<template>
  <div id="user-list">
    <v-card>
      <v-card-title> {{ getPageTitle() }} </v-card-title>
      <!-- actions -->

      <v-card-text class="d-flex space-between flex-wrap pb-0">
        <v-btn icon @click="toggleSearch">
          <v-icon>
            {{ icons.mdiMagnify }}
          </v-icon>
        </v-btn>
        <v-spacer></v-spacer>
        <div class="d-flex align-center flex-wrap">
          <v-btn color="primary" class="mb-4" :to="{ name: 'admin-news-form' }">
            <span>{{ $t("Uusi uutinen") }}</span>
          </v-btn>
        </div>
      </v-card-text>

      <v-card-text v-if="showSearch">
        <v-text-field v-model="searchQuery" :label="$t('Hae otsikolla')" dense outlined clearable></v-text-field>
      </v-card-text>

      <v-data-table :headers="tableColumns" :items="news" :server-items-length="totalCount" :items-per-page="10"
        :options.sync="options" :loading="loading" mobile-breakpoint="0" :footer-props="{
          'items-per-page-options': [10],
        }">
        <!-- title -->
        <template #[`item.title`]="{ item }">
          <div class="d-flex align-center">
            <router-link :to="{ name: 'admin-news-form', params: { id: item.id } }"
              class="font-weight-medium text-decoration-none">
              {{ item.title }}
            </router-link>
          </div>
        </template>

        <!-- group -->
        <template #[`item.group_name`]="{ item }">
          <v-chip label small color="primary">{{ item.group_name }}</v-chip>
        </template>

        <!-- published -->
        <template #[`item.published`]="{ item }">
          <div class="d-flex text-center">
            <v-icon v-if="item.published" size="24" :color="getPublishedColor(item)">{{ icons.mdiCheckboxMarkedCircle }}
            </v-icon>
            <v-icon v-else size="24" color="error">{{ icons.mdiCloseCircle }}
            </v-icon>
          </div>
        </template>

        <!-- publish date -->
        <template #[`item.publish_date`]="{ item }">
          <span>{{ getDateTime(item.release_date) }}</span>
        </template>

        <!-- polls replioes -->
        <template #[`item.poll_replies`]="{ item }">
          <router-link v-if="item.poll" :to="{
            name: 'admin-news-poll-replies',
            params: { newsId: item.id, pollId: item.poll },
          }" class="
              text--primary
              font-weight-semibold
              text-truncate
              cursor-pointer
              text-decoration-none
            ">
            {{ $t("Näytä vastaukset") }} &raquo;
          </router-link>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>


<script>
import { onMounted, ref, watch, computed } from "@vue/composition-api";
import adminApi from "@/api/admin";
import { mdiCheckboxMarkedCircle, mdiCloseCircle, mdiPlus, mdiMagnify } from "@mdi/js";
import { useTranslations } from "@core/libs/i18n";
import { getDateTime } from "@/@core/utils/datetime_helpers";
import { i18n } from "@/plugins/i18n";
import useMenus from "@/@core/utils/useMenus";
import useAuth from "@/useAuth";
import moment from "moment";

export default {
  setup() {
    const { tenant } = useAuth();
    const { getTenantTerm } = useMenus();
    const news = ref([]);
    const totalCount = ref(0);
    const searchQuery = ref("");
    const showSearch = ref(false);

    const page = ref(1);
    const options = ref({});
    const loading = ref(false);
    const error = ref("");

    const { t } = useTranslations();

    const tableColumns = computed(() => [
      { text: t("Otsikko").toUpperCase(), value: "title", sortable: false },
      { text: t("Ryhmä").toUpperCase(), value: "group_name", sortable: false },
      {
        text: t("Julkaistu").toUpperCase(),
        value: "published",
        sortable: false,
      },
      {
        text: t("Julkaisupäivä").toUpperCase(),
        value: "publish_date",
        sortable: false,
      },
      { text: "", value: "poll_replies", sortable: false },
    ]);

    // fetch data
    const fetchNews = async () => {
      loading.value = true;
      const response = await adminApi.getNewsList(page.value, 10, searchQuery.value);

      if (response.ok) {
        news.value = response.data.results;
        totalCount.value = response.data.count;
      }

      loading.value = false;
    };

    watch(options, () => {
      page.value = options.value.page;
      fetchNews();
    });

    let searchTimeout = null;
    watch(searchQuery, () => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        if (searchQuery.value) {
          if (searchQuery.value.length > 3) {
            options.value = { ...options.value, page: 1 };
            page.value = options.value.page;
          }
        }
        else {
          options.value = { ...options.value, page: 1 };
          page.value = options.value.page;
        }
      }, 600);
    });

    const getPageTitle = () => {
      const tenantTerm = getTenantTerm(
        "news",
        tenant.value.terms?.translations,
        i18n.locale
      );
      return tenantTerm ?? t("Uutiset");
    };

    const toggleSearch = () => {
      if (showSearch.value)
        searchQuery.value = "";
      showSearch.value = !showSearch.value;
    }

    const getPublishedColor = (newsItem) => {
      if (newsItem.release_date) {

        if (moment(new Date()).isAfter(newsItem.release_date))
          return 'success';
        return 'warning';
      }
      return 'success';
    }

    // UnRegister on leave
    onMounted(() => {
      // fetchNews();
    });

    return {
      getPageTitle,
      news,
      searchQuery,
      totalCount,
      toggleSearch,
      showSearch,
      tableColumns,
      options,
      loading,
      getPublishedColor,
      getDateTime,
      icons: {
        mdiCheckboxMarkedCircle,
        mdiCloseCircle,
        mdiPlus,
        mdiMagnify
      },
      t,
    };
  },
};
</script>

<style></style>
